/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { Mutex } from "async-mutex";
import store from "reducers/Store";
import { setPlayerPlaybackProgress } from "actions/PlayerActions";
import {
  buildNodes,
  GainNodeType,
  PlaylistItemNode,
  updateBackgroundGainNode,
} from "audioProcessing/BuildNodes";
import { calculateTotalDuration } from "utilities/ClipUtilities";
import AudioBufferCache from "audioProcessing/AudioBufferCache";
import { PlaylistClip } from "types/Clip";
import { secondsToMilliseconds } from "TimeHelper";
import { BlockTypes } from "constants/BlockTypes";
import isEqual from "lodash.isequal";
import { AnimationFrameTimeUpdater } from "utilities/TimeUpdater";

const urlParams = new URLSearchParams(location.search);
const debug = urlParams.get("debug") === "true";

export class LiveAudioContext {
  private readonly _cache: AudioBufferCache;
  private readonly _mutex = new Mutex();
  private _playlist: PlaylistClip[] = [];
  private _isPlaying = false;
  private readonly debug: boolean = false;
  private _activePlaybackCompletePromise: Promise<unknown[]> = Promise.resolve(
    []
  );
  readonly _timeUpdater: AnimationFrameTimeUpdater;

  duration = 0;
  _audioContext: AudioContext;
  contextStartTime = 0;
  _activeNodes: Promise<PlaylistItemNode>[] = [];

  constructor(
    cache: AudioBufferCache,
    audioContext: AudioContext = new AudioContext()
  ) {
    this.debug = debug;
    this._cache = cache;
    this._timeUpdater = new AnimationFrameTimeUpdater(this.dispatchProgress);
    this._audioContext = audioContext;
    this.log("AC created:", this._audioContext.state);
    this._audioContext.resume().then(async () => {
      this.log("AC initial resume:", this._audioContext.state);
      await this.suspendAudioContextIfPaused();
      this.log("AC initial pause:", this._audioContext.state);
    });
  }

  async play(): Promise<void> {
    this._isPlaying = true;
    this._timeUpdater.resume();
    this.log("AC play resume:", this._audioContext.state);
    await this._audioContext.resume();
    this.log("AC play resume done:", this._audioContext.state);
  }

  async pause(): Promise<void> {
    this._isPlaying = false;
    this.log("AC pause suspend:", this._audioContext.state);
    if (this._audioContext.state === "suspended") {
      return;
    }
    await this.suspendAudioContextIfPaused();
    this.log("AC pause suspend done:", this._audioContext.state);
    this._timeUpdater.suspend();
  }

  get isPlaying(): boolean {
    return this._isPlaying;
  }

  get playbackComplete(): boolean {
    return this.duration - this.elapsedTime < this._audioContext.baseLatency;
  }

  get elapsedTime(): number {
    return Math.max(this.currentTime - this.contextStartTime, 0);
  }

  get currentTime(): number {
    return secondsToMilliseconds(this._audioContext.currentTime);
  }

  async setPlaylist(playlist: PlaylistClip[]): Promise<void> {
    const releaseLock = await this._mutex.acquire();
    try {
      this._stopNodes();
    } finally {
      releaseLock();
    }
    this._playlist = playlist;
    this.duration = calculateTotalDuration(playlist);
    this.dispatchProgress();
  }

  get playlist(): PlaylistClip[] {
    return this._playlist;
  }

  async _stopNodes(): Promise<void> {
    const nodes = this._activeNodes;
    this._activeNodes = [];

    await Promise.all(
      nodes.map(async (nodePromise) => {
        const node = await nodePromise;

        for (const gain of node.gains) {
          gain.node.disconnect();
        }

        const originalSourceNode = node.sourceNodePair.original;
        originalSourceNode.onended = null;
        originalSourceNode.disconnect();

        const enhancedSourceNode = node.sourceNodePair.enhanced;
        if (enhancedSourceNode) {
          enhancedSourceNode.onended = null;
          enhancedSourceNode.disconnect();
        }
      })
    );
  }

  // we only hotswap playlist changes that only involve changing the background gain
  // of the different clips. This is initially to support the strength slider
  // in Enhance Speech. Background gain doesn't change the start/stop time of clips
  // so it's significantly easier to update.
  private canHotSwapPlaylist(newPlaylist: PlaylistClip[]): boolean {
    if (newPlaylist.length !== this._playlist.length) {
      return false;
    }

    return newPlaylist.every((newClip, idx): boolean => {
      const oldClip = this._playlist[idx];

      if (!oldClip) {
        return false;
      }

      if (isEqual(oldClip, newClip)) {
        return true;
      }

      const bothWaveform =
        newClip.type === BlockTypes.WAVEFORM &&
        oldClip.type === BlockTypes.WAVEFORM;
      const bothTranscription =
        newClip.type === BlockTypes.TRANSCRIPTION &&
        oldClip.type === BlockTypes.TRANSCRIPTION;

      if (bothWaveform) {
        return isEqual(
          { ...oldClip, backgroundGain: newClip.backgroundGain },
          newClip
        );
      } else if (bothTranscription) {
        return isEqual(
          { ...oldClip, enhancementInfo: newClip.enhancementInfo },
          newClip
        );
      }

      return false;
    });
  }

  async hotSwapPlaylist(
    newPlaylist: PlaylistClip[]
  ): Promise<HotSwapType | null> {
    if (isEqual(newPlaylist, this._playlist)) {
      // short circuit if playlists are exactly the same
      return HotSwapType.noop;
    }

    if (!this.canHotSwapPlaylist(newPlaylist)) {
      return null;
    }

    const nodes = await Promise.all(this._activeNodes);
    newPlaylist.forEach((clip) => {
      if (
        ![BlockTypes.WAVEFORM, BlockTypes.TRANSCRIPTION].includes(clip.type)
      ) {
        return;
      }

      const itemNode = nodes.find((itemNode) => itemNode.clipId === clip.id);

      if (!itemNode) {
        return;
      }

      if (clip.type === BlockTypes.WAVEFORM && clip.backgroundGain) {
        const backgroundNode = itemNode.gains.find(
          (gain) => gain.type === GainNodeType.background
        );

        if (!backgroundNode) {
          return;
        }

        updateBackgroundGainNode(
          this._audioContext,
          backgroundNode.node as GainNode,
          clip.backgroundGain
        );
      } else if (
        clip.type === BlockTypes.TRANSCRIPTION &&
        clip.enhancementInfo
      ) {
        const enhancedSourceGain = itemNode.gains.find(
          (gain) => gain.type === GainNodeType.enhanceSource
        );

        const originalSourceGain = itemNode.gains.find(
          (gain) => gain.type === GainNodeType.originalSource
        );

        if (!enhancedSourceGain || !originalSourceGain) {
          return;
        }

        (enhancedSourceGain.node as GainNode).gain.value =
          clip.enhancementInfo.strengthPercent / 100;
        (originalSourceGain.node as GainNode).gain.value =
          1 - clip.enhancementInfo.strengthPercent / 100;
      }
    });

    this._playlist = newPlaylist;

    return HotSwapType.hotswap;
  }

  reset(): void {
    this._audioContext.close();
    this._audioContext = new AudioContext();
  }

  async queuePlaylist(
    seekTime: number,
    onPlaybackComplete: VoidFunction
  ): Promise<void> {
    const releaseLock = await this._mutex.acquire();
    try {
      await this._stopNodes();

      this.contextStartTime = this.currentTime - seekTime;
      this.dispatchProgress();
      const nodes = buildNodes(
        this._audioContext,
        this.playlist,
        this._cache,
        seekTime
      );

      this._activeNodes = nodes;

      if (!nodes.length) {
        return;
      }

      const playbackReadyPromise = Promise.all(this._activeNodes).then(
        async (nodes) => {
          await this.suspendAudioContextIfPaused();

          return nodes;
        }
      );

      this._registerPlaybackCompletePromise(nodes, onPlaybackComplete);

      return playbackReadyPromise.then(() => {});
    } finally {
      releaseLock();
    }
  }

  async _registerPlaybackCompletePromise(
    nodes: Promise<PlaylistItemNode>[],
    onPlaybackComplete: VoidFunction
  ): Promise<void> {
    const playbackCompletePromise = Promise.all(
      nodes.map((nodePromise) => {
        return new Promise(async (resolve) => {
          const node = await nodePromise;
          const source = node.sourceNodePair.original;
          source.onended = resolve;
        });
      })
    );

    this._activePlaybackCompletePromise = playbackCompletePromise;

    await playbackCompletePromise;

    if (playbackCompletePromise !== this._activePlaybackCompletePromise) {
      // Another promise has become active so ignore
      return;
    }

    console.log("Finished playback");
    onPlaybackComplete();
  }

  dispatchProgress = (): void => {
    store.dispatch(setPlayerPlaybackProgress(this.elapsedTime, this.duration));
  };

  private suspendAudioContextIfPaused = async (): Promise<void> => {
    this.log(
      "AC suspendAudioContextIfPaused:",
      this._isPlaying,
      this._audioContext.state
    );
    if (this._isPlaying || this._audioContext.state === "suspended") {
      return;
    }

    this.log(
      "AC suspendAudioContextIfPaused suspend:",
      this._isPlaying,
      this._audioContext.state
    );
    await this._audioContext.suspend();
    this.log(
      "AC suspendAudioContextIfPaused suspend done:",
      this._isPlaying,
      this._audioContext.state
    );
  };

  private log(...args: any[]): void {
    if (this.debug) {
      console.log(...args);
    }
  }
}

export enum HotSwapType {
  noop,
  hotswap,
}
